import {
  FaStar,
  FaRegStar,
  FaToilet,
  FaParking,
  FaTv,
  FaWifi,
  FaFan,
  FaBath,
  FaLock,
} from "react-icons/fa";
import { FaStarHalfStroke } from "react-icons/fa6";
import { APP_COLORS } from "../styles/system";
import {
  MdAir,
  MdBalcony,
  MdShower,
  MdBathtub,
  MdLiveTv,
  MdLaptop,
  MdLocalBar,
  MdBeachAccess,
  MdPool,
  MdPark,
  MdGarage,
  MdElevator,
  MdWaterDrop,
  MdWbSunny,
  MdSecurity,
  MdVideocam,
  MdOutdoorGrill,
  MdFitnessCenter,
  MdKitchen,
  MdLocalLaundryService,
  MdWeekend,
} from "react-icons/md";
import { FaBolt } from "react-icons/fa";
import { parse } from "libphonenumber-js";

export const isFieldWithValue = (field) => field?.replace(/\s/g, "")?.length;

// Coordonnées de contact de la plateforme (support client)
export const PLATFORM_CONTACT = {
  phone: "+221769494041",
  phoneDisplay: "+221 76 949 40 41",
  email: "support@dalal.sn",
  get whatsappUrl() {
    return `https://wa.me/${this.phone.replace(/[^0-9]/g, "")}`;
  },
  get telUrl() {
    return `tel:${this.phone}`;
  },
  get mailtoUrl() {
    return `mailto:${this.email}`;
  },
};

export const formatPrice = (value = "") =>
  isFieldWithValue(value)
    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    : "";

// Valeurs de nom manifestement fausses saisies "pour tester"
export const FAKE_NAME_BLOCKLIST = [
  "test", "tests", "teste", "essai", "essaie", "azerty", "qwerty", "asdf",
  "abc", "abcd", "xxx", "aaa", "toto", "tata", "titi", "tutu", "fake",
  "demo", "user", "client", "anonyme", "inconnu", "nom", "prenom", "prénom",
  "name", "lorem", "ipsum", "na", "nan",
];

// Vérifie qu'un nom complet ressemble à un vrai nom (et non "test", "aaaa"...)
export const isValidFullName = (value = "") => {
  const name = `${value}`.trim().toLowerCase();
  if (name.length < 3) return false;
  // pas de chiffres dans un nom
  if (/\d/.test(name)) return false;
  // au moins une voyelle
  if (!/[aeiouyàâäéèêëïîôöùûüæœ]/i.test(name)) return false;
  // un même caractère répété (aaaa, zzzz)
  if (/^(.)\1+$/.test(name.replace(/\s+/g, ""))) return false;
  // les noms mononymes sont acceptés ; on filtre juste les mots trop courts
  const words = name.split(/[\s-]+/).filter(Boolean);
  if (words.some((w) => w.replace(/[^a-zà-ÿæœ]/gi, "").length < 2)) return false;
  // un des mots figure dans la liste de valeurs bidon
  if (words.some((w) => FAKE_NAME_BLOCKLIST.includes(w))) return false;
  return true;
};

// Détecte une suite strictement croissante/décroissante (123456, 987654)
const isSequentialDigits = (digits = "") => {
  if (digits.length < 4) return false;
  let asc = true;
  let desc = true;
  for (let i = 1; i < digits.length; i++) {
    const diff = digits.charCodeAt(i) - digits.charCodeAt(i - 1);
    if (diff !== 1) asc = false;
    if (diff !== -1) desc = false;
  }
  return asc || desc;
};

// Filtre les numéros "conformes" mais irréalistes (770000000, 771111111...)
// `nationalNumber` = numéro sans indicatif pays (chiffres uniquement)
export const isRealisticPhoneNumber = (nationalNumber = "") => {
  const digits = `${nationalNumber}`.replace(/\D/g, "");
  if (digits.length < 6) return false;
  // tous les chiffres identiques (0000000, 7777777)
  if (/^(\d)\1+$/.test(digits)) return false;
  // longue série de zéros (770000000)
  if (/0{5,}/.test(digits)) return false;
  // longue répétition d'un même chiffre (771111111)
  if (/(\d)\1{5,}/.test(digits)) return false;
  // suite séquentielle (123456789)
  if (isSequentialDigits(digits)) return false;
  return true;
};

export const renderStar = (value = 0) => {
  switch (value) {
    case 1:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 1.5:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStarHalfStroke
            size={14}
            color={APP_COLORS.SECONDARY_COLOR.color}
          />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 2:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 2.5:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStarHalfStroke
            size={14}
            color={APP_COLORS.SECONDARY_COLOR.color}
          />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 3:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 3.5:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStarHalfStroke
            size={14}
            color={APP_COLORS.SECONDARY_COLOR.color}
          />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 4:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaRegStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    case 4.5:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStarHalfStroke
            size={14}
            color={APP_COLORS.SECONDARY_COLOR.color}
          />
        </>
      );
    case 5:
      return (
        <>
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
          <FaStar size={14} color={APP_COLORS.SECONDARY_COLOR.color} />
        </>
      );
    default:
      return (
        <>
          {/* <FontAwesome name="star-o" size={14} color="black" />
                <FontAwesome name="star-o" size={14} color="black" />
                <FontAwesome name="star-o" size={14} color="black" />
                <FontAwesome name="star-o" size={14} color="black" />
                <FontAwesome name="star-o" size={14} color="black" /> */}
        </>
      );
  }
};

export const magicNumber = (value) => {
  try {
    console.log(Math.round(1.6180339 * 10));
    return Math.round(1.6180339 * value);
  } catch (error) {
    console.log(error);
    return value;
  }
};

export const isMobileOrSmallScreen = () =>
  /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

export const WindowsSizeBreakpoints = {
  small: 576,
  medium: 768,
  large: 992,
  xlarge: 1200,
};

export const truncateText = (text = "", endTo = 25) =>
  text?.length > endTo ? `${text.substr(0, endTo)}...` : text;

export const getPlaceAddress = (place) => {
  try {
    if (isFieldWithValue(place?.house?.address)) return truncateText(place.house.address, 25)
    const city = `${place?.house?.position?.extras?.address?.city || ""}`;
    const suburb = `${place?.house?.position?.extras?.address?.suburb || ""}`;
    return truncateText(`${city}, ${suburb}`, 25);
  } catch (error) {
    console.log({ error });
    return "";
  }
};

export const EPlaceProperties = {
  Air_Conditioner: "Climatisation",
  Toilet: "Toilette",
  Wifi: "Wifi",
  Balcony: "Balcon",
  Parking: "Parking",
  TV: "TV",
  Fan: "Ventilateur",
};

export const EPlacePropertiesServer = {
  Air_Conditioner: "Air_Conditioner",
  Toilet: "Toilet",
  Wifi: "Wifi",
  Balcony: "Balcony",
  Parking: "Parking",
  TV: "TV",
  Fan: "Fan",
};

export const renderIconProperties = (
  label = "",
  color = "black",
  size = 14
) => {
  switch (label) {
    case EPlacePropertiesServer.Air_Conditioner:
      return <MdAir size={size} color={color} />;
    case EPlacePropertiesServer.Toilet:
      return <FaToilet size={size} color={color} />;
    case EPlacePropertiesServer.Balcony:
      return <MdBalcony size={size} color={color} />;
    case EPlacePropertiesServer.Parking:
      return <FaParking size={size} color={color} />;
    case EPlacePropertiesServer.TV:
      return <FaTv size={size} color={color} />;
    case EPlacePropertiesServer.Wifi:
      return <FaWifi size={size} color={color} />;
    case EPlacePropertiesServer.Fan:
      return <FaFan size={size} color={color} />;
    default:
      console.log({ label });
      return null;
  }
};

export const ERoomAmenity = {
  AIR_CONDITIONING: "AIR_CONDITIONING",
  CEILING_FAN: "CEILING_FAN",
  HOT_WATER: "HOT_WATER",
  PRIVATE_BATHROOM: "PRIVATE_BATHROOM",
  SHARED_BATHROOM: "SHARED_BATHROOM",
  PRIVATE_TOILET: "PRIVATE_TOILET",
  JACUZZI_BATHTUB: "JACUZZI_BATHTUB",
  SMART_TV: "SMART_TV",
  IPTV_CHANNELS: "IPTV_CHANNELS",
  DEDICATED_WIFI: "DEDICATED_WIFI",
  DESK_WORKSPACE: "DESK_WORKSPACE",
  BALCONY_TERRACE: "BALCONY_TERRACE",
  MINI_BAR: "MINI_BAR",
  SAFE_BOX: "SAFE_BOX",
  SEA_VIEW: "SEA_VIEW",
  POOL_VIEW: "POOL_VIEW",
  GARDEN_VIEW: "GARDEN_VIEW",
};

export const ERoomAmenityLabels = {
  AIR_CONDITIONING: "Climatisation",
  CEILING_FAN: "Ventilateur plafond",
  HOT_WATER: "Eau chaude",
  PRIVATE_BATHROOM: "Salle de bain privée",
  SHARED_BATHROOM: "Salle de bain partagée",
  PRIVATE_TOILET: "Toilettes privées",
  JACUZZI_BATHTUB: "Jacuzzi / Baignoire",
  SMART_TV: "Smart TV",
  IPTV_CHANNELS: "Chaînes IPTV",
  DEDICATED_WIFI: "Wifi dédié",
  DESK_WORKSPACE: "Bureau de travail",
  BALCONY_TERRACE: "Balcon / Terrasse",
  MINI_BAR: "Mini-bar",
  SAFE_BOX: "Coffre-fort",
  SEA_VIEW: "Vue mer",
  POOL_VIEW: "Vue piscine",
  GARDEN_VIEW: "Vue jardin",
};

export const renderAmenityIcon = (amenity = "", color = "black", size = 14) => {
  switch (amenity) {
    case ERoomAmenity.AIR_CONDITIONING:
      return <MdAir size={size} color={color} />;
    case ERoomAmenity.CEILING_FAN:
      return <FaFan size={size} color={color} />;
    case ERoomAmenity.HOT_WATER:
      return <MdShower size={size} color={color} />;
    case ERoomAmenity.PRIVATE_BATHROOM:
    case ERoomAmenity.SHARED_BATHROOM:
      return <FaBath size={size} color={color} />;
    case ERoomAmenity.PRIVATE_TOILET:
      return <FaToilet size={size} color={color} />;
    case ERoomAmenity.JACUZZI_BATHTUB:
      return <MdBathtub size={size} color={color} />;
    case ERoomAmenity.SMART_TV:
      return <FaTv size={size} color={color} />;
    case ERoomAmenity.IPTV_CHANNELS:
      return <MdLiveTv size={size} color={color} />;
    case ERoomAmenity.DEDICATED_WIFI:
      return <FaWifi size={size} color={color} />;
    case ERoomAmenity.DESK_WORKSPACE:
      return <MdLaptop size={size} color={color} />;
    case ERoomAmenity.BALCONY_TERRACE:
      return <MdBalcony size={size} color={color} />;
    case ERoomAmenity.MINI_BAR:
      return <MdLocalBar size={size} color={color} />;
    case ERoomAmenity.SAFE_BOX:
      return <FaLock size={size} color={color} />;
    case ERoomAmenity.SEA_VIEW:
      return <MdBeachAccess size={size} color={color} />;
    case ERoomAmenity.POOL_VIEW:
      return <MdPool size={size} color={color} />;
    case ERoomAmenity.GARDEN_VIEW:
      return <MdPark size={size} color={color} />;
    default:
      return null;
  }
};

export const EHouseAmenity = {
  SECURE_PARKING: "SECURE_PARKING",
  PRIVATE_GARAGE: "PRIVATE_GARAGE",
  ELEVATOR: "ELEVATOR",
  POWER_GENERATOR: "POWER_GENERATOR",
  WATER_TANK: "WATER_TANK",
  SOLAR_PANELS: "SOLAR_PANELS",
  SECURITY_24_7: "SECURITY_24_7",
  CCTV_CAMERAS: "CCTV_CAMERAS",
  SWIMMING_POOL: "SWIMMING_POOL",
  GARDEN_COURTYARD: "GARDEN_COURTYARD",
  BBQ_AREA: "BBQ_AREA",
  GYM_FITNESS: "GYM_FITNESS",
  SHARED_KITCHEN: "SHARED_KITCHEN",
  LAUNDRY_WASHING_MACHINE: "LAUNDRY_WASHING_MACHINE",
  SHARED_LIVING_ROOM: "SHARED_LIVING_ROOM",
};

export const EHouseAmenityLabels = {
  SECURE_PARKING: "Parking sécurisé",
  PRIVATE_GARAGE: "Garage privé",
  ELEVATOR: "Ascenseur",
  POWER_GENERATOR: "Groupe électrogène",
  WATER_TANK: "Château d'eau",
  SOLAR_PANELS: "Panneaux solaires",
  SECURITY_24_7: "Sécurité 24h/7j",
  CCTV_CAMERAS: "Caméras CCTV",
  SWIMMING_POOL: "Piscine",
  GARDEN_COURTYARD: "Jardin / Cour",
  BBQ_AREA: "Espace barbecue",
  GYM_FITNESS: "Salle de sport",
  SHARED_KITCHEN: "Cuisine commune",
  LAUNDRY_WASHING_MACHINE: "Lave-linge",
  SHARED_LIVING_ROOM: "Salon commun",
};

export const renderHouseAmenityIcon = (amenity = "", color = "black", size = 14) => {
  switch (amenity) {
    case EHouseAmenity.SECURE_PARKING:
      return <FaParking size={size} color={color} />;
    case EHouseAmenity.PRIVATE_GARAGE:
      return <MdGarage size={size} color={color} />;
    case EHouseAmenity.ELEVATOR:
      return <MdElevator size={size} color={color} />;
    case EHouseAmenity.POWER_GENERATOR:
      return <FaBolt size={size} color={color} />;
    case EHouseAmenity.WATER_TANK:
      return <MdWaterDrop size={size} color={color} />;
    case EHouseAmenity.SOLAR_PANELS:
      return <MdWbSunny size={size} color={color} />;
    case EHouseAmenity.SECURITY_24_7:
      return <MdSecurity size={size} color={color} />;
    case EHouseAmenity.CCTV_CAMERAS:
      return <MdVideocam size={size} color={color} />;
    case EHouseAmenity.SWIMMING_POOL:
      return <MdPool size={size} color={color} />;
    case EHouseAmenity.GARDEN_COURTYARD:
      return <MdPark size={size} color={color} />;
    case EHouseAmenity.BBQ_AREA:
      return <MdOutdoorGrill size={size} color={color} />;
    case EHouseAmenity.GYM_FITNESS:
      return <MdFitnessCenter size={size} color={color} />;
    case EHouseAmenity.SHARED_KITCHEN:
      return <MdKitchen size={size} color={color} />;
    case EHouseAmenity.LAUNDRY_WASHING_MACHINE:
      return <MdLocalLaundryService size={size} color={color} />;
    case EHouseAmenity.SHARED_LIVING_ROOM:
      return <MdWeekend size={size} color={color} />;
    default:
      return null;
  }
};

export const ERoomTypes = {
  ALL: "Tous les types",
  ROOM: "Chambre",
  APPARTMENT: "Appartement",
  SUITE: "Suite",
};

export const EPlaceTypes = {
  ROOM: "Chambre",
  APPARTMENT: "Appartement",
  SUITE: "Suite",
  HOUSE: "Maison",
};

export const ERoomTypesServer = {
  ROOM: "ROOM",
  APPARTMENT: "APPARTMENT",
  SUITE: "SUITE",
};

export const COUNTRIES = [
  { name: "Senegal", code: "221", iso: "SN" },
  { name: "Afghanistan", code: "93", iso: "AF" },
  { name: "Albania", code: "355", iso: "AL" },
  { name: "Algeria", code: "213", iso: "DZ" },
  { name: "American Samoa", code: "1-684", iso: "AS" },
  { name: "Andorra", code: "376", iso: "AD" },
  { name: "Angola", code: "244", iso: "AO" },
  { name: "Anguilla", code: "1-264", iso: "AI" },
  { name: "Antarctica", code: "672", iso: "AQ" },
  { name: "Antigua and Barbuda", code: "1-268", iso: "AG" },
  { name: "Argentina", code: "54", iso: "AR" },
  { name: "Armenia", code: "374", iso: "AM" },
  { name: "Aruba", code: "297", iso: "AW" },
  { name: "Australia", code: "61", iso: "AU" },
  { name: "Austria", code: "43", iso: "AT" },
  { name: "Azerbaijan", code: "994", iso: "AZ" },
  { name: "Bahamas", code: "1-242", iso: "BS" },
  { name: "Bahrain", code: "973", iso: "BH" },
  { name: "Bangladesh", code: "880", iso: "BD" },
  { name: "Barbados", code: "1-246", iso: "BB" },
  { name: "Belarus", code: "375", iso: "BY" },
  { name: "Belgium", code: "32", iso: "BE" },
  { name: "Belize", code: "501", iso: "BZ" },
  { name: "Benin", code: "229", iso: "BJ" },
  { name: "Bermuda", code: "1-441", iso: "BM" },
  { name: "Bhutan", code: "975", iso: "BT" },
  { name: "Bolivia", code: "591", iso: "BO" },
  { name: "Bosnia and Herzegovina", code: "387", iso: "BA" },
  { name: "Botswana", code: "267", iso: "BW" },
  { name: "Brazil", code: "55", iso: "BR" },
  { name: "British Indian Ocean Territory", code: "246", iso: "IO" },
  { name: "British Virgin Islands", code: "1-284", iso: "VG" },
  { name: "Brunei", code: "673", iso: "BN" },
  { name: "Bulgaria", code: "359", iso: "BG" },
  { name: "Burkina Faso", code: "226", iso: "BF" },
  { name: "Burundi", code: "257", iso: "BI" },
  { name: "Cambodia", code: "855", iso: "KH" },
  { name: "Cameroon", code: "237", iso: "CM" },
  { name: "Canada", code: "1", iso: "CA" },
  { name: "Cape Verde", code: "238", iso: "CV" },
  { name: "Cayman Islands", code: "1-345", iso: "KY" },
  { name: "Central African Republic", code: "236", iso: "CF" },
  { name: "Chad", code: "235", iso: "TD" },
  { name: "Chile", code: "56", iso: "CL" },
  { name: "China", code: "86", iso: "CN" },
  { name: "Christmas Island", code: "61", iso: "CX" },
  { name: "Cocos Islands", code: "61", iso: "CC" },
  { name: "Colombia", code: "57", iso: "CO" },
  { name: "Comoros", code: "269", iso: "KM" },
  { name: "Cook Islands", code: "682", iso: "CK" },
  { name: "Costa Rica", code: "506", iso: "CR" },
  { name: "Croatia", code: "385", iso: "HR" },
  { name: "Cuba", code: "53", iso: "CU" },
  { name: "Curacao", code: "599", iso: "CW" },
  { name: "Cyprus", code: "357", iso: "CY" },
  { name: "Czech Republic", code: "420", iso: "CZ" },
  { name: "Democratic Republic of the Congo", code: "243", iso: "CD" },
  { name: "Denmark", code: "45", iso: "DK" },
  { name: "Djibouti", code: "253", iso: "DJ" },
  { name: "Dominica", code: "1-767", iso: "DM" },
  { name: "Dominican Republic", code: "1-809, 1-829, 1-849", iso: "DO" },
  { name: "East Timor", code: "670", iso: "TL" },
  { name: "Ecuador", code: "593", iso: "EC" },
  { name: "Egypt", code: "20", iso: "EG" },
  { name: "El Salvador", code: "503", iso: "SV" },
  { name: "Equatorial Guinea", code: "240", iso: "GQ" },
  { name: "Eritrea", code: "291", iso: "ER" },
  { name: "Estonia", code: "372", iso: "EE" },
  { name: "Ethiopia", code: "251", iso: "ET" },
  { name: "Falkland Islands", code: "500", iso: "FK" },
  { name: "Faroe Islands", code: "298", iso: "FO" },
  { name: "Fiji", code: "679", iso: "FJ" },
  { name: "Finland", code: "358", iso: "FI" },
  { name: "France", code: "33", iso: "FR" },
  { name: "French Polynesia", code: "689", iso: "PF" },
  { name: "Gabon", code: "241", iso: "GA" },
  { name: "Gambia", code: "220", iso: "GM" },
  { name: "Georgia", code: "995", iso: "GE" },
  { name: "Germany", code: "49", iso: "DE" },
  { name: "Ghana", code: "233", iso: "GH" },
  { name: "Gibraltar", code: "350", iso: "GI" },
  { name: "Greece", code: "30", iso: "GR" },
  { name: "Greenland", code: "299", iso: "GL" },
  { name: "Grenada", code: "1-473", iso: "GD" },
  { name: "Guam", code: "1-671", iso: "GU" },
  { name: "Guatemala", code: "502", iso: "GT" },
  { name: "Guernsey", code: "44-1481", iso: "GG" },
  { name: "Guinea", code: "224", iso: "GN" },
  { name: "Guinea-Bissau", code: "245", iso: "GW" },
  { name: "Guyana", code: "592", iso: "GY" },
  { name: "Haiti", code: "509", iso: "HT" },
  { name: "Honduras", code: "504", iso: "HN" },
  { name: "Hong Kong", code: "852", iso: "HK" },
  { name: "Hungary", code: "36", iso: "HU" },
  { name: "Iceland", code: "354", iso: "IS" },
  { name: "India", code: "91", iso: "IN" },
  { name: "Indonesia", code: "62", iso: "ID" },
  { name: "Iran", code: "98", iso: "IR" },
  { name: "Iraq", code: "964", iso: "IQ" },
  { name: "Ireland", code: "353", iso: "IE" },
  { name: "Isle of Man", code: "44-1624", iso: "IM" },
  { name: "Israel", code: "972", iso: "IL" },
  { name: "Italy", code: "39", iso: "IT" },
  { name: "Ivory Coast", code: "225", iso: "CI" },
  { name: "Jamaica", code: "1-876", iso: "JM" },
  { name: "Japan", code: "81", iso: "JP" },
  { name: "Jersey", code: "44-1534", iso: "JE" },
  { name: "Jordan", code: "962", iso: "JO" },
  { name: "Kazakhstan", code: "7", iso: "KZ" },
  { name: "Kenya", code: "254", iso: "KE" },
  { name: "Kiribati", code: "686", iso: "KI" },
  { name: "Kosovo", code: "383", iso: "XK" },
  { name: "Kuwait", code: "965", iso: "KW" },
  { name: "Kyrgyzstan", code: "996", iso: "KG" },
  { name: "Laos", code: "856", iso: "LA" },
  { name: "Latvia", code: "371", iso: "LV" },
  { name: "Lebanon", code: "961", iso: "LB" },
  { name: "Lesotho", code: "266", iso: "LS" },
  { name: "Liberia", code: "231", iso: "LR" },
  { name: "Libya", code: "218", iso: "LY" },
  { name: "Liechtenstein", code: "423", iso: "LI" },
  { name: "Lithuania", code: "370", iso: "LT" },
  { name: "Luxembourg", code: "352", iso: "LU" },
  { name: "Macau", code: "853", iso: "MO" },
  { name: "Macedonia", code: "389", iso: "MK" },
  { name: "Madagascar", code: "261", iso: "MG" },
  { name: "Malawi", code: "265", iso: "MW" },
  { name: "Malaysia", code: "60", iso: "MY" },
  { name: "Maldives", code: "960", iso: "MV" },
  { name: "Mali", code: "223", iso: "ML" },
  { name: "Malta", code: "356", iso: "MT" },
  { name: "Marshall Islands", code: "692", iso: "MH" },
  { name: "Mauritania", code: "222", iso: "MR" },
  { name: "Mauritius", code: "230", iso: "MU" },
  { name: "Mayotte", code: "262", iso: "YT" },
  { name: "Mexico", code: "52", iso: "MX" },
  { name: "Micronesia", code: "691", iso: "FM" },
  { name: "Moldova", code: "373", iso: "MD" },
  { name: "Monaco", code: "377", iso: "MC" },
  { name: "Mongolia", code: "976", iso: "MN" },
  { name: "Montenegro", code: "382", iso: "ME" },
  { name: "Montserrat", code: "1-664", iso: "MS" },
  { name: "Morocco", code: "212", iso: "MA" },
  { name: "Mozambique", code: "258", iso: "MZ" },
  { name: "Myanmar", code: "95", iso: "MM" },
  { name: "Namibia", code: "264", iso: "NA" },
  { name: "Nauru", code: "674", iso: "NR" },
  { name: "Nepal", code: "977", iso: "NP" },
  { name: "Netherlands", code: "31", iso: "NL" },
  { name: "Netherlands Antilles", code: "599", iso: "AN" },
  { name: "New Caledonia", code: "687", iso: "NC" },
  { name: "New Zealand", code: "64", iso: "NZ" },
  { name: "Nicaragua", code: "505", iso: "NI" },
  { name: "Niger", code: "227", iso: "NE" },
  { name: "Nigeria", code: "234", iso: "NG" },
  { name: "Niue", code: "683", iso: "NU" },
  { name: "North Korea", code: "850", iso: "KP" },
  { name: "Northern Mariana Islands", code: "1-670", iso: "MP" },
  { name: "Norway", code: "47", iso: "NO" },
  { name: "Oman", code: "968", iso: "OM" },
  { name: "Pakistan", code: "92", iso: "PK" },
  { name: "Palau", code: "680", iso: "PW" },
  { name: "Palestine", code: "970", iso: "PS" },
  { name: "Panama", code: "507", iso: "PA" },
  { name: "Papua New Guinea", code: "675", iso: "PG" },
  { name: "Paraguay", code: "595", iso: "PY" },
  { name: "Peru", code: "51", iso: "PE" },
  { name: "Philippines", code: "63", iso: "PH" },
  { name: "Pitcairn", code: "64", iso: "PN" },
  { name: "Poland", code: "48", iso: "PL" },
  { name: "Portugal", code: "351", iso: "PT" },
  { name: "Puerto Rico", code: "1-787, 1-939", iso: "PR" },
  { name: "Qatar", code: "974", iso: "QA" },
  { name: "Republic of the Congo", code: "242", iso: "CG" },
  { name: "Reunion", code: "262", iso: "RE" },
  { name: "Romania", code: "40", iso: "RO" },
  { name: "Russia", code: "7", iso: "RU" },
  { name: "Rwanda", code: "250", iso: "RW" },
  { name: "Saint Barthelemy", code: "590", iso: "BL" },
  { name: "Saint Helena", code: "290", iso: "SH" },
  { name: "Saint Kitts and Nevis", code: "1-869", iso: "KN" },
  { name: "Saint Lucia", code: "1-758", iso: "LC" },
  { name: "Saint Martin", code: "590", iso: "MF" },
  { name: "Saint Pierre and Miquelon", code: "508", iso: "PM" },
  { name: "Saint Vincent and the Grenadines", code: "1-784", iso: "VC" },
  { name: "Samoa", code: "685", iso: "WS" },
  { name: "San Marino", code: "378", iso: "SM" },
  { name: "Sao Tome and Principe", code: "239", iso: "ST" },
  { name: "Saudi Arabia", code: "966", iso: "SA" },
  { name: "Serbia", code: "381", iso: "RS" },
  { name: "Seychelles", code: "248", iso: "SC" },
  { name: "Sierra Leone", code: "232", iso: "SL" },
  { name: "Singapore", code: "65", iso: "SG" },
  { name: "Sint Maarten", code: "1-721", iso: "SX" },
  { name: "Slovakia", code: "421", iso: "SK" },
  { name: "Slovenia", code: "386", iso: "SI" },
  { name: "Solomon Islands", code: "677", iso: "SB" },
  { name: "Somalia", code: "252", iso: "SO" },
  { name: "South Africa", code: "27", iso: "ZA" },
  { name: "South Korea", code: "82", iso: "KR" },
  { name: "South Sudan", code: "211", iso: "SS" },
  { name: "Spain", code: "34", iso: "ES" },
  { name: "Sri Lanka", code: "94", iso: "LK" },
  { name: "Sudan", code: "249", iso: "SD" },
  { name: "Suriname", code: "597", iso: "SR" },
  { name: "Svalbard and Jan Mayen", code: "47", iso: "SJ" },
  { name: "Swaziland", code: "268", iso: "SZ" },
  { name: "Sweden", code: "46", iso: "SE" },
  { name: "Switzerland", code: "41", iso: "CH" },
  { name: "Syria", code: "963", iso: "SY" },
  { name: "Taiwan", code: "886", iso: "TW" },
  { name: "Tajikistan", code: "992", iso: "TJ" },
  { name: "Tanzania", code: "255", iso: "TZ" },
  { name: "Thailand", code: "66", iso: "TH" },
  { name: "Togo", code: "228", iso: "TG" },
  { name: "Tokelau", code: "690", iso: "TK" },
  { name: "Tonga", code: "676", iso: "TO" },
  { name: "Trinidad and Tobago", code: "1-868", iso: "TT" },
  { name: "Tunisia", code: "216", iso: "TN" },
  { name: "Turkey", code: "90", iso: "TR" },
  { name: "Turkmenistan", code: "993", iso: "TM" },
  { name: "Turks and Caicos Islands", code: "1-649", iso: "TC" },
  { name: "Tuvalu", code: "688", iso: "TV" },
  { name: "U.S. Virgin Islands", code: "1-340", iso: "VI" },
  { name: "Uganda", code: "256", iso: "UG" },
  { name: "Ukraine", code: "380", iso: "UA" },
  { name: "United Arab Emirates", code: "971", iso: "AE" },
  { name: "United Kingdom", code: "44", iso: "GB" },
  { name: "United States", code: "1", iso: "US" },
  { name: "Uruguay", code: "598", iso: "UY" },
  { name: "Uzbekistan", code: "998", iso: "UZ" },
  { name: "Vanuatu", code: "678", iso: "VU" },
  { name: "Vatican", code: "379", iso: "VA" },
  { name: "Venezuela", code: "58", iso: "VE" },
  { name: "Vietnam", code: "84", iso: "VN" },
  { name: "Wallis and Futuna", code: "681", iso: "WF" },
  { name: "Western Sahara", code: "212", iso: "EH" },
  { name: "Yemen", code: "967", iso: "YE" },
  { name: "Zambia", code: "260", iso: "ZM" },
  { name: "Zimbabwe", code: "263", iso: "ZW" },
];

export const getFlagEmoji = (countryCode) => {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

export const getCountryName = (countryCode) => {
  const country = COUNTRIES.find((country) => country.code === countryCode);
  return country ? country.name : "Unknown";
};

export const EAccountType = {
  DEFAULT: "DEFAULT",
  ADMIN: "ADMIN",
  SUPERVISOR: "SUPERVISOR",
  ADMIN_PLATFORM: "ADMIN_PLATFORM",
  SUPER_ADMIN: "SUPER_ADMIN",
};

export const ERoomStatus = {
  AVAILABLE: "AVAILABLE",
  TAKEN: "TAKEN",
  OFF: "OFF",
};

export const MenuItem = {
  Dashboard: "Dashboard",
  OwnerDashboard: "OwnerDashboard",
  Houses: "Houses",
  Rooms: "Rooms",
  Reservations: "Reservations",
  CancelledReservations: "CancelledReservations",
  Messages: "Messages",
  Partners: "Partners",
  Listings: "Listings",
  Reviews: "Reviews",
  Complaints: "Complaints",
  Settings: "Settings",
};

// Statuts d'une réclamation. Le helper getComplaintStatus reste tolérant :
// toute valeur inconnue renvoyée par l'API est affichée telle quelle.
export const EComplaintStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  RESOLVED: "RESOLVED",
  REJECTED: "REJECTED",
};

// Type d'auteur d'une réclamation : ADMIN (gérant) ou CLIENT.
export const getComplaintAuthorLabel = (authorType) => {
  switch (authorType) {
    case "ADMIN":
      return "Gérant";
    case "CLIENT":
      return "Client";
    default:
      return isFieldWithValue(authorType) ? authorType : "—";
  }
};

// Transitions de statut autorisées depuis le footer. RESOLVED et REJECTED
// sont terminaux (aucune action proposée).
export const getComplaintNextStatuses = (status) => {
  switch (status) {
    case EComplaintStatus.OPEN:
      return [
        EComplaintStatus.IN_PROGRESS,
        EComplaintStatus.RESOLVED,
        EComplaintStatus.REJECTED,
      ];
    case EComplaintStatus.IN_PROGRESS:
      return [EComplaintStatus.RESOLVED, EComplaintStatus.REJECTED];
    default:
      return [];
  }
};

export const getComplaintStatusLabel = (status) => {
  switch (status) {
    case EComplaintStatus.OPEN:
      return "Ouverte";
    case EComplaintStatus.IN_PROGRESS:
      return "En cours";
    case EComplaintStatus.RESOLVED:
      return "Résolue";
    case EComplaintStatus.REJECTED:
      return "Rejetée";
    default:
      return isFieldWithValue(status) ? status : "—";
  }
};

export const getComplaintStatus = (status) => {
  const base =
    "text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap";
  switch (status) {
    case EComplaintStatus.RESOLVED:
      return <span className={`bg-green-100 text-green-800 ${base}`}>Résolue</span>;
    case EComplaintStatus.IN_PROGRESS:
      return <span className={`bg-yellow-100 text-yellow-800 ${base}`}>En cours</span>;
    case EComplaintStatus.REJECTED:
      return <span className={`bg-red-100 text-red-800 ${base}`}>Rejetée</span>;
    case EComplaintStatus.OPEN:
      return <span className={`bg-blue-100 text-blue-800 ${base}`}>Ouverte</span>;
    default:
      return (
        <span className={`bg-gray-100 text-gray-600 ${base}`}>
          {isFieldWithValue(status) ? status : "—"}
        </span>
      );
  }
};

export const EListingType = {
  PREMIUM: "PREMIUM",
  GOLD: "GOLD",
  STANDARD: "STANDARD",
};

export const EListingStatus = {
  DRAFT: 'DRAFT',       // créée mais non publiée
  ACTIVE: 'ACTIVE',     // publiée et visible
  PAUSED: 'PAUSED',     // temporairement suspendue par le propriétaire
  EXPIRED: 'EXPIRED',   // durée de vie écoulée
  CLOSED: 'CLOSED',     // clôturée manuellement
  BANNED: 'BANNED',     // bannie/bloquée par un admin plateforme
};

export const EDevise = {
  FCFA: "FCFA",
  // EUR: "EUR",
  // USD: "USD",
};

export const EReservationStatus = {
  ON_REQUEST: "ON_REQUEST",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  VALIDATED: "VALIDATED",
  IN_PROGRESS: "IN_PROGRESS",
  ENDED: "ENDED",
  CLOSED: "CLOSED",
};

export const EReservationCancelledBy = {
  CLIENT: "CLIENT",
  COMPANY: "COMPANY",
};

export const getReservationCancelledByLabel = (cancelledBy) => {
  switch (cancelledBy) {
    case EReservationCancelledBy.CLIENT:
      return "Client";
    case EReservationCancelledBy.COMPANY:
      return "Compagnie";
    default:
      return "—";
  }
};

// Après annulation, client et admin disposent de 48h pour déposer une
// réclamation (demande de remboursement). Même règle que le backend,
// qui reste la référence : ce contrôle côté UI évite juste d'envoyer
// une requête vouée à l'échec.
export const COMPLAINT_AFTER_CANCEL_DEADLINE_HOURS = 48;

export const getComplaintDeadline = (reservation) => {
  if (reservation?.status !== EReservationStatus.CANCELLED) return null;
  // Repli sur lastUpdatedAt pour les réservations annulées avant l'ajout de cancelledAt
  const cancelledAt = reservation.cancelledAt ?? reservation.lastUpdatedAt;
  if (!cancelledAt) return null;
  return new Date(
    new Date(cancelledAt).getTime() +
      COMPLAINT_AFTER_CANCEL_DEADLINE_HOURS * 60 * 60 * 1000,
  );
};

export const isComplaintDeadlinePassed = (reservation) => {
  const deadline = getComplaintDeadline(reservation);
  return !!deadline && Date.now() > deadline.getTime();
};

export const EPaymentStatus = {
  PENDING: "pending",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  FAILED: "failed",
  CANCELLED_SYSTEM: "cancelled_system",
};

export const EPaymentType = {
  ADVANCE: "advance",
  COMPLEMENT: "complement",
};

export const getReservationStatus = (status) => {
  try {
    switch (status) {
      case EReservationStatus.ENDED:
        return (
          <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300">
            Terminée
          </span>
        );
      case EReservationStatus.IN_PROGRESS:
        return (
          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-yellow-900 dark:text-yellow-300">
            En cours
          </span>
        );
      case EReservationStatus.CANCELLED:
        return (
          <span className="bg-red-100 text-red-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
            Annulée
          </span>
        );
      case EReservationStatus.VALIDATED:
        return (
          <span className="bg-[#005248] text-white text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
            Validée
          </span>
        );
      case EReservationStatus.ON_REQUEST:
        return (
          <span className="bg-gray-600 text-white text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
            En attente
          </span>
        );
      case EReservationStatus.ACCEPTED:
        return (
          <span className="bg-secondary text-white text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
            Acceptée
          </span>
        );
        case EReservationStatus.REJECTED:
        return (
          <span className="bg-red-100 text-red-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
            Rejetée
          </span>
        );
      case EReservationStatus.CLOSED:
        return (
          <span className="bg-red-100 text-red-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
            Fermée
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
            Unknown
          </span>
        );
    }
  } catch (error) {
    console.log({ error });
  }
};

const MOIS = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];

export const formatDateShort = (date) => {
  try {
    return `${date.getDate()} ${MOIS[date.getMonth()]}`;
  } catch {
    return "";
  }
};

export const formatDateToDDMMYYYY = (date) => {
  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getDate()).padStart(2, "0");
    if (!day || !year || !month) return "-";
    return `${day}/${month}/${year}`;
  } catch (error) {
    return "";
  }
};

const defineDayOf = (dayValue, date) => {
  const mois = [
    "Janv.",
    "Fév.",
    "Mars.",
    "Avr.",
    "Mai",
    "Juin",
    "Juil.",
    "Août",
    "Sept.",
    "Oct",
    "Nov",
    "Dec",
  ];
  const minutes =
    date?.getMinutes() > 9 ? date?.getMinutes() : "0" + date?.getMinutes();
  const hours =
    date?.getHours() > 9 ? date?.getHours() : "0" + date?.getHours();
  switch (dayValue) {
    case 1:
      return "Hier à " + hours + ":" + minutes;
    default:
      return (
        date?.getDate() +
        " " +
        mois[date?.getMonth()] +
        " " +
        date?.getFullYear()
        // +
        // " à " +
        // hours +
        // ":" +
        // minutes
      );
  }
};

export const timing = (date, notAdd = false) => {
  try {
    var today = new Date();
    var dtime = new Date(date.toString());
    var diffMs = today - dtime; // milliseconds between now & Christmas
    var diffDays = Math.floor(diffMs / 86400000); // days
    var diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
    var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
    if (diffDays > 0) {
      return defineDayOf(diffDays, dtime);
    } else {
      if (diffHrs > 0) {
        return notAdd ? diffHrs + "h" : "il y'a " + diffHrs + "h";
      } else {
        if (diffMins < 1) {
          return "à l'instant";
        } else {
          return notAdd ? diffMins + " min." : "il y'a " + diffMins + " min.";
        }
      }
    }
  } catch (error) {
    console.log({ error });
    return "";
  }
};

export const getUserPhoneAndCountryCode = (phone) => {
  try {
    const parsedNumber = parse(phone);
    const country = COUNTRIES.find((c) => c.iso === parsedNumber.country);
    return {
      phone: parsedNumber.phone,
      countryCode: country.code || "221",
      country,
    };
  } catch (error) {
    return {
      phone: "",
      countryCode: "",
      country: null,
    };
  }
};

export const isDateMoreThanOneYear = (dateToCheck) => {
  try {
    const today = new Date();
    const oneYearLater = new Date(today.setFullYear(today.getFullYear() + 1));
    return dateToCheck > oneYearLater;
  } catch (error) {
    console.log({ error })
    return true;
  }
};

// ---- Périodes bloquées d'une annonce ----
// Les dates arrivent du serveur à minuit UTC : on garde la partie YYYY-MM-DD
export const blockedDateToInputStr = (value) => {
  try {
    if (!value) return "";
    return new Date(value).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// "2026-11-10" -> Date locale à minuit (pour les calendriers)
export const inputStrToDate = (value = "") => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Date locale -> "2026-11-10"
export const dateToInputStr = (date) => {
  if (!(date instanceof Date)) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const addDaysToInputStr = (value, days) => {
  const date = inputStrToDate(value);
  date.setDate(date.getDate() + days);
  return dateToInputStr(date);
};

// "2026-11-10" -> "10 nov. 2026"
export const formatInputDateStr = (value = "") => {
  try {
    if (!value) return "";
    const [year, month, day] = value.split("-").map(Number);
    return `${day} ${MOIS[month - 1]} ${year}`;
  } catch {
    return "";
  }
};

// Même règle que l'API : seules les nuits comptent. On peut partir le premier
// jour bloqué et arriver le lendemain du dernier. Un séjour sans nuit occupe
// son jour d'arrivée. Dates au format YYYY-MM-DD (comparables en chaîne).
export const isStayInBlockedPeriods = (blockedPeriods = [], startDate, endDate) => {
  if (!blockedPeriods?.length || !startDate || !endDate) return false;
  const hasNights = endDate > startDate;
  return blockedPeriods.some((p) => {
    const blockStart = blockedDateToInputStr(p.startDate);
    const blockEnd = blockedDateToInputStr(p.endDate);
    const lastNightAfterBlockStart = hasNights ? endDate > blockStart : startDate >= blockStart;
    return startDate <= blockEnd && lastNightAfterBlockStart;
  });
};

export const convertDateStringFormat = (value = "") => {
  try {
    if (isFieldWithValue(value)) {
      const parts = value.split("-");
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return "";
  } catch (error) {
    console.log({ error });
    return "";
  }
};

export const ERequestCompanyStatus = {
  ON_REQUEST: "ON_REQUEST",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
};

export const EAllowedAccountDashboard = [ "ADMIN", "SUPERVISOR","ADMIN_PLATFORM","SUPER_ADMIN",];

export const getCompanyFilter = (user) => {
  try {
    switch(user?.account_type) {
      case EAccountType.ADMIN:
      case EAccountType.SUPERVISOR:
        return [user.company.code];
      case EAccountType.ADMIN_PLATFORM:
      case EAccountType.SUPER_ADMIN:
        return [];
      default: 
        return [null];
    }
  } catch (error) {
    return [null];
  }
}


export const UNTREATED_STATUSES = [EReservationStatus.ON_REQUEST, EReservationStatus.ACCEPTED];

// export const isReservationObsolete = (reservation) => {
//   if (!UNTREATED_STATUSES.includes(reservation?.status)) return false;
//   const startDate = reservation?.realStartDate || reservation?.startDate;
//   if (!startDate) return false;
//   return new Date(startDate) < new Date(Date.now() - 86400000);
// };

export const isReservationObsolete = (reservation) => {
  if (!UNTREATED_STATUSES.includes(reservation?.status)) return false;
  const startDate = reservation?.realStartDate || reservation?.startDate;
  if (!startDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  // Obsolète si le client devait arriver avant aujourd'hui
  return start < today;
};

export const hasValue = (value) => value !== null && value !== undefined;